<script lang="ts" setup>
import type { OfferImage } from '~/types/Catalog';
import { getSelectedMulticardOffer, isMulticardV2Offer } from '~/utils/multicard';

const { alt, isFreeDeliveryAvailable } = defineProps<{ alt: string; isFreeDeliveryAvailable?: boolean }>();

const catalogStore = useCatalogStore();
const { selectedOffer, selectedSizeId } = storeToRefs(catalogStore);
const { isCatalogScrollRestorationPending } = useCatalogScrollRestoration();

const selectedSlideIndex = ref(0);
const isShowFullscreenGallery = ref(false);

const activeOffer = computed(() => {
  if (isMulticardV2Offer(selectedOffer.value)) {
    return getSelectedMulticardOffer(selectedOffer.value, selectedSizeId.value);
  }

  return selectedOffer.value;
});

const images = computed<OfferImage[]>(() => {
  const offer = activeOffer.value;

  if (!offer) return [];

  return [...(offer.offer_images ?? []), ...selectedOffer.value.product_images].sort(
    (a, b) => Number(b.is_main) - Number(a.is_main),
  );
});
const confirmedImages = useState<OfferImage[]>('product-page-confirmed-images', () => []);

watch(
  images,
  (value) => {
    if (value.length) confirmedImages.value = value;
  },
  { immediate: true },
);

const displayedImages = computed(() => {
  if (images.value.length) return images.value;

  return isCatalogScrollRestorationPending.value ? confirmedImages.value : [];
});

const carouselVideoLink = computed(() => {
  if (isMulticardV2Offer(selectedOffer.value)) {
    return activeOffer.value?.carousel_video_link ?? null;
  }

  return selectedOffer.value.carousel_video_link;
});

watch(
  () => activeOffer.value?.id,
  () => {
    selectedSlideIndex.value = 0;
    isShowFullscreenGallery.value = false;
  },
);
</script>

<template>
  <section v-if="selectedOffer">
    <ProductPageImageSlider
      v-model:selected-slide-index="selectedSlideIndex"
      :alt
      :images="displayedImages"
      :is-free-delivery-available="isFreeDeliveryAvailable"
      :video="carouselVideoLink"
      @open-fullscreen="isShowFullscreenGallery = true"
    />

    <FullscreenImageGallery
      v-model:is-show="isShowFullscreenGallery"
      v-model:selected-slide-index="selectedSlideIndex"
      :alt
      :images="displayedImages"
      :video="carouselVideoLink"
    />
  </section>
</template>
